import React from 'react';

import { UserEntity } from 'Models/Entities';
import { highlightMatches } from 'Util/HighlightUtils';
import { upperCaseFirst } from 'Util/StringUtils';
import { stringNotEmpty } from 'Util/TypeGuards';

export function formatCustomerName(user: UserEntity): string {
	// When the customer has a company, show it in brackets next to their name in customer pickers
	// (e.g. "John Smith (Amity Trader)"). Shown whenever a company is present, regardless of config.
	const company = stringNotEmpty(user.companyName) ? ` (${user.companyName.trim()})` : '';
	return (
		`${upperCaseFirst((user.firstName ?? '').toLowerCase())} ` +
		`${upperCaseFirst((user.lastName ?? '').toLowerCase())}` +
		`${company}` +
		`${user.isGuest ? ' (Guest)' : ''}`
	);
}

export function formatCustomerDescription(user: UserEntity): string {
	const parts = [user.email];
	if (user.phone) {
		parts.push(user.phone);
	}
	const joined = parts.filter(Boolean).join(' · ');
	return user.disabled ? `${joined} - DEACTIVATED ACCOUNT` : joined;
}

export function sortCustomersByName(a: UserEntity, b: UserEntity): number {
	const firstCmp = (a.firstName ?? '').localeCompare(b.firstName ?? '', undefined, { sensitivity: 'base' });
	if (firstCmp !== 0) {
		return firstCmp;
	}
	return (a.lastName ?? '').localeCompare(b.lastName ?? '', undefined, { sensitivity: 'base' });
}

export interface FormatOptionLabelMeta {
	context: 'menu' | 'value';
	inputValue: string;
}

/**
 * Shared `formatOptionLabel` for react-select customer pickers. Renders a
 * highlighted two-line layout (name above email · phone) in the dropdown
 * menu and a plain name string for the closed-trigger selected value.
 */
export function formatCustomerOption(option: UserEntity, meta: FormatOptionLabelMeta): React.ReactNode {
	const displayName = formatCustomerName(option);
	if (meta.context === 'value') {
		return displayName;
	}
	const descriptionText = formatCustomerDescription(option);
	const query = meta.inputValue ?? '';
	return (
		// translate="no": names, emails and phone numbers are not prose, and Google Translate
		// rewriting them both garbles the result and mutates the DOM React is updating per keystroke.
		<div className="flex flex-col" translate="no">
			<span className="text-sm text-gray-900">{highlightMatches(displayName, query)}</span>
			<span className="text-xs text-gray-500">{highlightMatches(descriptionText, query)}</span>
		</div>
	);
}
